import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiKeys, createApiKey, rotateApiKey, revokeApiKey, getApiUsage, apiError } from "@/lib/api";
import { PageHeader, Card, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { KeyRound, Plus, Copy, RotateCcw, ShieldAlert, Sparkles, Activity, Check } from "lucide-react";

function SecretDialog({ secret, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(secret); toast.success("Copied to clipboard"); };
  return (
    <Dialog open={!!secret} onOpenChange={onClose}>
      <DialogContent data-testid="api-secret-dialog">
        <DialogHeader>
          <DialogTitle>Your new API key</DialogTitle>
          <DialogDescription>Copy it now — for your security it will never be shown again.</DialogDescription>
        </DialogHeader>
        <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-[#D4AF37] break-all" data-testid="api-secret-value">{secret}</div>
        <DialogFooter>
          <Button onClick={copy} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="copy-secret-btn"><Copy className="w-4 h-4 mr-2" /> Copy key</Button>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApiManagement() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getApiKeys().then((d) => { setData(d); if (d.api_enabled) getApiUsage().then(setUsage).catch(() => {}); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <Loading />;

  if (!data.api_enabled) {
    return (
      <div data-testid="api-page">
        <PageHeader title="API Management" subtitle="Programmatic access to your WiseAce workspace" />
        <Card className="p-10 text-center max-w-xl mx-auto" data-testid="api-upsell">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center mx-auto text-[#8a6d0f]"><Sparkles className="w-7 h-7" /></div>
          <h3 className="font-display text-xl font-bold text-[#0B132B] mt-4">API access is available on the Pro plan</h3>
          <p className="text-slate-500 text-sm mt-2">Upgrade to Pro to generate secure API keys and integrate WiseAce with your own systems.</p>
          <Button onClick={() => nav("/billing")} className="mt-6 bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="api-upgrade-btn">Upgrade to Pro</Button>
        </Card>
      </div>
    );
  }

  const submitCreate = async () => {
    if (!name.trim()) return toast.error("Give your key a name");
    setBusy(true);
    try {
      const res = await createApiKey(name.trim());
      setSecret(res.secret); setCreateOpen(false); setName(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const doRotate = async (id) => {
    try { const res = await rotateApiKey(id); setSecret(res.secret); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doRevoke = async (id) => {
    try { await revokeApiKey(id); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const activeKeys = data.keys.filter((k) => k.status === "active");

  return (
    <div data-testid="api-page">
      <PageHeader title="API Management" subtitle="Generate keys and monitor usage — Pro plan">
        <Button onClick={() => setCreateOpen(true)} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="create-key-btn">
          <Plus className="w-4 h-4 mr-2" /> Create API Key
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Check className="w-4 h-4" /> API Access</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]">Enabled</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Activity className="w-4 h-4" /> Requests today</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]" data-testid="usage-today">{usage?.today ?? 0}</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Activity className="w-4 h-4" /> This month</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]" data-testid="usage-month">{usage?.this_month ?? 0}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 font-semibold text-[#0B132B] flex items-center gap-2"><KeyRound className="w-4 h-4" /> API Keys</div>
        {activeKeys.length === 0 ? (
          <div className="px-5 py-10 text-center text-slate-400 text-sm" data-testid="no-keys">No API keys yet. Create one to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="api-keys-table">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Key</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 font-semibold">Last used</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeKeys.map((k) => (
                  <tr key={k.id} className="border-b border-slate-50 last:border-0" data-testid={`key-row-${k.id}`}>
                    <td className="px-5 py-4 font-medium text-[#0B132B]">{k.name}</td>
                    <td className="px-5 py-4 font-mono text-slate-500">{k.masked}</td>
                    <td className="px-5 py-4 text-slate-500">{fmtDate(k.created_at)}</td>
                    <td className="px-5 py-4 text-slate-500">{k.last_used_at ? fmtDate(k.last_used_at) : "Never"}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" onClick={() => doRotate(k.id)} className="mr-2" data-testid={`rotate-key-${k.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Rotate</Button>
                      <ConfirmDelete
                        onConfirm={() => doRevoke(k.id)}
                        title="Revoke this API key?"
                        description="Applications using this key will immediately stop working."
                        testId={`revoke-key-dialog-${k.id}`}
                        trigger={<Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid={`revoke-key-${k.id}`}>Revoke</Button>}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {usage?.recent?.length > 0 && (
        <Card className="overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-slate-100 font-semibold text-[#0B132B]">Recent Requests</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="api-usage-table">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Endpoint</th>
                  <th className="px-5 py-3 font-semibold">Method</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Time</th>
                  <th className="px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {usage.recent.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-3 font-mono text-slate-600">{r.endpoint}</td>
                    <td className="px-5 py-3 text-slate-500">{r.method}</td>
                    <td className="px-5 py-3"><span className={r.status < 400 ? "text-emerald-600" : "text-rose-600"}>{r.status}</span></td>
                    <td className="px-5 py-3 text-slate-500">{r.response_ms} ms</td>
                    <td className="px-5 py-3 text-slate-500">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ShieldAlertNote />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Name this key so you can recognise it later.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="key-name">Key name</Label>
            <Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Zapier integration" data-testid="key-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={busy} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="submit-key-btn">{busy ? "Creating…" : "Create key"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretDialog secret={secret} onClose={() => setSecret("")} />
    </div>
  );
}

function ShieldAlertNote() {
  return (
    <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
      <ShieldAlert className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <span>Keep API keys secret. Requests are rate-limited to 100 per minute per key and are scoped to your business only. Base URL: <span className="font-mono">/api/v1</span> — authenticate with header <span className="font-mono">X-API-Key</span>.</span>
    </div>
  );
}
